import React from 'react';
import { Helmet } from 'react-helmet-async';
import {
  Box,
  Container,
  Typography,
  Divider,
} from '@mui/material';
import PublicPageShell from '../components/PublicPageShell';
import LegalBusinessDetails, { AI_RAMS_DISCLAIMER } from '../components/LegalBusinessDetails';

const PRIMARY = '#0087c1';

const Section = ({ number, title, children }) => (
  <Box sx={{ mb: 5 }}>
    <Typography variant="h5" component="h2" fontWeight={700} sx={{ mb: 2, color: 'text.primary' }}>
      {number}. {title}
    </Typography>
    {children}
  </Box>
);

const Paragraph = ({ children, sx }) => (
  <Typography variant="body1" sx={{ lineHeight: 1.8, color: 'text.secondary', mb: 2, ...sx }}>
    {children}
  </Typography>
);

const BulletList = ({ items }) => (
  <Box component="ul" sx={{ pl: 3, mb: 2 }}>
    {items.map((item, i) => (
      <Box component="li" key={i} sx={{ mb: 1 }}>
        <Typography variant="body1" sx={{ lineHeight: 1.8, color: 'text.secondary' }}>
          {item}
        </Typography>
      </Box>
    ))}
  </Box>
);

export default function TermsAndConditions() {
  return (
    <PublicPageShell>
      <Helmet>
        <title>Terms &amp; Conditions — Struxor</title>
        <meta name="description" content="Read the Terms and Conditions for using Struxor, the construction software platform for project management, digital forms, timesheets, and site collaboration." />
        <link rel="canonical" href="https://www.struxorapp.com/terms" />
        <meta property="og:title" content="Terms & Conditions — Struxor" />
        <meta property="og:description" content="Read the Terms and Conditions for using Struxor, the construction software platform for project management, digital forms, timesheets, and site collaboration." />
        <meta property="og:url" content="https://www.struxorapp.com/terms" />
      </Helmet>

      {/* Hero */}
      <Box sx={{ background: `linear-gradient(135deg, ${PRIMARY} 0%, #005a8c 100%)`, color: '#fff', py: { xs: 6, md: 8 }, textAlign: 'center' }}>
        <Container maxWidth="md">
          <Typography variant="h3" component="h1" fontWeight={800} sx={{ fontSize: { xs: '2rem', md: '2.75rem' }, mb: 2 }}>
            Terms &amp; Conditions
          </Typography>
          <Typography variant="h6" sx={{ opacity: 0.9, fontWeight: 400, maxWidth: 550, mx: 'auto', fontSize: { xs: '1rem', md: '1.15rem' } }}>
            The rules that govern your use of Struxor.
          </Typography>
        </Container>
      </Box>

      {/* Content */}
      <Container maxWidth="md" sx={{ py: { xs: 5, md: 7 } }}>
        <Paragraph sx={{ mb: 4, fontStyle: 'italic' }}>Last updated: 6 April 2026</Paragraph>

        <Paragraph>
          These Terms govern your use of Struxor, the subscription software service provided by <strong>Luke Rutley Ltd</strong>
          trading as Struxor ("we", "us", "our"). Luke Rutley Ltd is the contracting entity for all Struxor subscriptions.
          By using Struxor, you agree to these Terms. If you do not agree, you must not use the Service.
        </Paragraph>

        <Box sx={{ mb: 5, p: 3, border: '1px solid', borderColor: 'divider', borderRadius: 3, bgcolor: 'background.paper' }}>
          <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1.5 }}>
            Company Information
          </Typography>
          <LegalBusinessDetails />
        </Box>

        <Divider sx={{ my: 5 }} />

        {/* 1 */}
        <Section number={1} title="The Service">
          <Paragraph>
            Struxor is a subscription-based cloud construction software and project management platform that provides project tracking,
            vendor management, file storage, PDF generation, and an AI assistant.
          </Paragraph>
          <Paragraph>
            We may update or improve the Service from time to time. Some features may be subject to fair use limits.
          </Paragraph>
        </Section>

        {/* 2 */}
        <Section number={2} title="Accounts & Access">
          <BulletList items={[
            'You must provide accurate information when registering.',
            'You are responsible for safeguarding your login details.',
            'You may not share accounts outside of your organisation.',
            'We may suspend accounts that breach these Terms.',
          ]} />
        </Section>

        {/* 3 */}
        <Section number={3} title="Subscription, Pricing & Payment">
          <BulletList items={[
            <>All plans begin with a <strong>14-day free trial</strong>.</>,
            'Subscriptions are billed monthly in advance and renew automatically until cancelled.',
            'Pricing is shown on our website and may change with notice.',
            'Prices exclude VAT. Any VAT charged will be shown separately before payment and on your invoice.',
            'Payment is processed securely by our payment provider.',
          ]} />
        </Section>

        {/* 4 */}
        <Section number={4} title="Cancellation & Refunds">
          <BulletList items={[
            'You can cancel anytime via your account settings.',
            'Subscriptions remain active until the end of the billing period.',
            <>Except where required by law, payments are <strong>non-refundable</strong> once a billing cycle has started.</>,
          ]} />
        </Section>

        {/* 5 */}
        <Section number={5} title="Use of the AI Assistant">
          <BulletList items={[
            'The AI assistant generates outputs based on your inputs. These may not always be accurate, complete, or suitable for your circumstances.',
            AI_RAMS_DISCLAIMER,
            'You must not treat AI outputs as legal, regulatory, engineering, or health-and-safety advice without your own review.',
            'You must not use the AI assistant for unlawful or misleading purposes.',
          ]} />
        </Section>

        {/* 6 */}
        <Section number={6} title="Acceptable Use">
          <Paragraph>You must not:</Paragraph>
          <BulletList items={[
            'Upload unlawful, harmful, or infringing content.',
            "Attempt to breach or disrupt the Service's security.",
            'Resell or redistribute the Service without written permission.',
          ]} />
        </Section>

        {/* 7 */}
        <Section number={7} title="Data & Privacy">
          <BulletList items={[
            'You own the content and files you upload.',
            <>Luke Rutley Ltd is the <strong>data controller</strong> for website visitor data, account data, billing data, and support correspondence.</>,
            <>Customer organisations remain the <strong>data controllers</strong> for customer-uploaded workspace data, and we act as a <strong>data processor</strong> for that data when providing the Service.</>,
            <>Where we process personal data on your behalf, our Data Processing Agreement (&ldquo;DPA&rdquo;) forms part of these Terms. By using the Service, you agree to the DPA. A copy is available on request.</>,
            'Processing is governed by our Privacy Policy.',
          ]} />
        </Section>

        {/* 8 */}
        <Section number={8} title="Storage & Fair Use">
          <Paragraph>
            Plans include defined storage and AI usage limits. Excessive use may require an upgrade.
          </Paragraph>
        </Section>

        {/* 9 */}
        <Section number={9} title="Intellectual Property">
          <Paragraph>
            We retain ownership of the platform, code, and brand. You retain ownership of your data.
            You are granted a non-exclusive licence to use Struxor while subscribed.
          </Paragraph>
        </Section>

        {/* 10 */}
        <Section number={10} title="Availability & Support">
          <Paragraph>
            We strive for high availability but do not guarantee uninterrupted access.
            Support is provided via the channels on our website.
          </Paragraph>
        </Section>

        {/* 11 */}
        <Section number={11} title="Limitation of Liability">
          <Paragraph>To the fullest extent permitted by law:</Paragraph>
          <BulletList items={[
            'We are not liable for indirect or consequential losses.',
            'We are not responsible for losses arising from your failure to review, approve, or adapt AI-generated outputs before use.',
            'Our total liability for any claim is limited to the fees paid by you in the 12 months prior to the claim.',
          ]} />
        </Section>

        {/* 12 */}
        <Section number={12} title="Termination">
          <Paragraph>
            We may suspend or terminate access if you breach these Terms.
            You may export your data before termination.
          </Paragraph>
        </Section>

        {/* 13 */}
        <Section number={13} title="Governing Law">
          <Paragraph>
            These Terms are governed by the laws of England and Wales.
            Disputes are subject to the exclusive jurisdiction of the courts of England and Wales.
          </Paragraph>
        </Section>

        <Section number={14} title="Contact & Notices">
          <Paragraph>
            Formal notices and service enquiries should be sent to Luke Rutley Ltd using the details below and will be acknowledged within 5 business days.
          </Paragraph>
          <Box sx={{ p: 3, border: '1px solid', borderColor: 'divider', borderRadius: 3, bgcolor: 'background.paper' }}>
            <LegalBusinessDetails />
          </Box>
        </Section>
      </Container>
    </PublicPageShell>
  );
}
